import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { CabinetService } from 'src/app/domain/cabinet/service/cabinet.service';
import { UploadComponent } from '../../../shared/component/upload/upload.component';
import { InputComponent } from '../../../shared/component/inputs/input-component/input.component';
import { ButtonComponent } from '../../../shared/component/buttons/button.component';
import { ProfileDTO, UserUpdateDTO } from '../../../user/profile/dto/ProfileDTO';
import { RegExpService } from '../../../../util/common/reg-exp.service';
import { CheckboxComponent } from "../../../shared/component/checkbox/checkbox.component";
import { ShowUIService } from "../../../../core/service/show-ui.service";
import {NOTIFICATIONS, NotificationsService} from "../../../../core/service/notifications.service";

@Component({
  selector: 'app-cabinet',
  templateUrl: './cabinet.component.html',
  styleUrls: ['./cabinet.component.scss'],
  imports: [
    ReactiveFormsModule,
    InputComponent,
    ButtonComponent,
    UploadComponent,
    CheckboxComponent,
  ],
})
export class CabinetComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  private cabinetService = inject(CabinetService);
  private showUIService = inject(ShowUIService);
  private notificationService = inject(NotificationsService);

  protected readonly RegExpService = RegExpService;

  @ViewChild('upload') uploadComponent: UploadComponent;

  profile: ProfileDTO;

  updateProfileForm = new FormGroup({
    name: new FormControl<string>('', { validators: [Validators.required, Validators.minLength(1)] }),
    company: new FormControl<string>('', { validators: [Validators.required, Validators.minLength(1)] }),
    position: new FormControl<string>('', { validators: [Validators.required, Validators.minLength(1)] }),
    phone: new FormControl<string>('', { validators: [Validators.required, Validators.pattern(RegExpService.phone)] }),
    email: new FormControl<string>({ value: '', disabled: true }),
    photo: new FormControl<string>(''),
    isMarketingAccepted: new FormControl<boolean>(false),
  });

  constructor() {
    this.showUIService.setShowSideNavBar(false);
  }

  ngOnInit(): void {
    this.cabinetService.profile
      .pipe(takeUntil(this._destroyStream))
      .subscribe((profile: ProfileDTO) => {
        if (!profile) return;
        this.profile = profile;
        this.createForm();
      });

    this.updateProfileForm.controls.phone.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        if (this.updateProfileForm.controls.phone.touched) {
          this.updateProfileForm.controls.phone.markAsUntouched();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async updateProfile(): Promise<void> {
    const awsImageName = await this.uploadComponent.upload('profile', this.profile.id + '.jpeg');
    if (awsImageName) {
      this.updateProfileForm.patchValue({ photo: awsImageName });
    }

    const updateDto: UserUpdateDTO = {
      name: this.updateProfileForm.value.name,
      company: this.updateProfileForm.value.company,
      position: this.updateProfileForm.value.position,
      phone: this.updateProfileForm.value.phone,
      photo: this.updateProfileForm.value.photo,
      isMarketingAccepted: this.updateProfileForm.value.isMarketingAccepted,
    };
    await firstValueFrom(this.cabinetService.updateUserProfile(updateDto)
      .pipe(takeUntil(this._destroyStream)));
    this.profile = { ...this.profile, ...updateDto };
    this.cabinetService.setProfile(this.profile);
    this.notificationService.success(NOTIFICATIONS.UPDATE_PROFILE_SUCCESS);
  }

  private createForm(): void {
    this.updateProfileForm.patchValue({
      name: this.profile.name,
      company: this.profile.company,
      position: this.profile.position,
      phone: this.profile.phone,
      email: this.profile.email,
      photo: this.profile.photo,
      isMarketingAccepted: this.profile.isMarketingAccepted,
    });
  }
}
